import { useSessionStore } from "@auth/sessionStore";
import {
  clinicBrandingClient,
  type ClinicBranding,
} from "@clinic/clinicBrandingClient";
import { clinicClient } from "@clinic/clinicClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

const clinicKeys = {
  all: ["clinic"] as const,
  detail: (clinicUuid: string) => ["clinic", clinicUuid] as const,
};

/** Non-suspense so app chrome (the document title) never blocks the shell. */
export const useClinic = () => {
  const clinicUuid = useSessionStore((s) => s.clinic?.clinicUuid ?? null);
  return useQuery({
    queryKey: clinicKeys.detail(clinicUuid ?? "anonymous"),
    queryFn: () => clinicClient.get(),
    enabled: clinicUuid !== null,
  });
};

export const useUpdateBranding = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (branding: ClinicBranding) =>
      clinicBrandingClient.setBranding(branding),
    meta: { action: "save branding" },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: clinicKeys.all }),
  });
};

export const useUploadClinicLogo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => clinicBrandingClient.uploadLogo(file),
    meta: { action: "upload the logo" },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: clinicKeys.all }),
  });
};

const BASE_TITLE = "Brian";

export const useClinicDocumentTitle = () => {
  const { data } = useClinic();
  useEffect(() => {
    document.title = data?.name ? `${data.name} · ${BASE_TITLE}` : BASE_TITLE;
  }, [data?.name]);
};
